import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useAutoApprovedQualifications } from "@src/appV2/Licenses/useAutoApprovedQualifications";
import { useGetQualifications } from "@src/appV2/Qualifications/api/useGetQualifications";
import { useOnboardingRequirement } from "@src/appV2/redesign/PlacementCandidate/hooks/useOnboardingRequirement";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { InterviewStatus } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { isAfter, isBefore, isSameDay, parseISO } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

import { useGetIncentives } from "../../../api/useGetIncentives";
import { type InterviewAvailability } from "../../../api/useGetInterviewAvailability";
import { type PlacementDetail } from "../../../api/useGetPlacement";
import { useHiringQualificationRanking } from "../../../api/useHiringQualificationRanking";
import { usePlacementRecruiterScreeningConfig } from "../../../hooks/usePlacementRecruiterScreeningConfig";
import { useTalkToRecruiterCopyText } from "../../../hooks/useTalkToRecruiterCopyText";
import { useHiringIncentiveProgram } from "../../../useHiringIncentiveProgram";
import { useHiringReliability } from "../../../useHiringReliability";
import { useIsHiringReferralEnabled } from "../../../useIsHiringReferralEnabled";
import { useIsJobInterviewsEnabled } from "../../../useIsJobInterviewsEnabled";
import { useJobRequestConfiguration } from "../../../useJobRequestConfiguration";
import { hasAppliedToPlacement } from "../../../utils";
import { getWorkplaceStartHour, isInterviewLateCancellation } from "../../../utils/reliabilityRule";
import { buildPrimaryActionParams } from "../utils/buildPrimaryActionParams";
import { getPrimaryAction } from "../utils/getPrimaryAction";

interface UsePlacementPrimaryActionParams {
  placementDetails: PlacementDetail;
  placementCandidate: PlacementCandidate;
  interviewAvailability: InterviewAvailability;
  isLoadingInterviewAvailability: boolean;
  maxReferralBonusAmount: number;
}

/**
 * Checks if profile should be considered completed for placement details page.
 * Profile is completed if any of these conditions are met:
 * - Profile is actually completed
 * - Highest ranking shared qualification is auto-approved
 * - Any placement worker type is auto-approved
 */
function checkIsProfileCompletedForPlacement(params: {
  isProfileCompleted: boolean;
  highestRankingSharedQualification: string | undefined;
  placementWorkerTypes: string[] | undefined;
  autoApprovedQualifications: Set<string>;
}): boolean {
  const {
    isProfileCompleted,
    highestRankingSharedQualification,
    placementWorkerTypes,
    autoApprovedQualifications,
  } = params;

  if (isProfileCompleted) {
    return true;
  }

  if (
    isDefined(highestRankingSharedQualification) &&
    autoApprovedQualifications.has(highestRankingSharedQualification)
  ) {
    return true;
  }

  if (isDefined(placementWorkerTypes)) {
    return placementWorkerTypes.some((workerType) => autoApprovedQualifications.has(workerType));
  }

  return false;
}

/**
 * Pure data gathering and action selection logic.
 * No side effects, no state mutations.
 */
export function usePlacementPrimaryAction(params: UsePlacementPrimaryActionParams) {
  const {
    placementDetails,
    placementCandidate,
    interviewAvailability,
    isLoadingInterviewAvailability,
    maxReferralBonusAmount,
  } = params;

  // Gather all required data via hooks
  const { tmz: workerTimezone, userId: workerId, shoppableQualifications } = useDefinedWorker();
  const isJobInterviewsEnabled = useIsJobInterviewsEnabled();
  const hiringReliability = useHiringReliability();
  const autoApprovedQualifications = useAutoApprovedQualifications();

  const { data: allQualificationsData, isLoading: isQualificationsLoading } = useGetQualifications({
    enabled: true,
  });
  const { hiringIncentiveProgram, incentiveProgram } = useHiringIncentiveProgram();
  const isHiringReferralEnabled = useIsHiringReferralEnabled();
  const { enabled: isJobRequestEnabled } = useJobRequestConfiguration(
    placementDetails.allowRequestInterview ?? false
  );
  const {
    isLoading: isOnboardingRequirementsLoading,
    isPending: isProfilePending,
    isInvalid: isProfileInvalid,
    isCompleted: isProfileCompleted,
    requirement,
  } = useOnboardingRequirement(workerId);
  const { data: signOnBonuses, isInitialLoading: isGetIncentivesLoading } = useGetIncentives(
    {
      workerId,
      queryParams: {
        filter: { sourceType: "Placement" },
      },
    },
    { enabled: hiringIncentiveProgram.enabled }
  );
  const { getHighestRankingQualification } = useHiringQualificationRanking();
  const talkToRecruiterCopy = useTalkToRecruiterCopyText();
  const worker = useDefinedWorker();
  const isInterviewLocationPermissionsCheckEnabled = useCbhFlag(
    CbhFeatureFlag.INTERVIEW_LOCATION_PERMISSIONS,
    { defaultValue: false }
  );
  const isInterviewLocationPermissionsRequired = useCbhFlag(
    CbhFeatureFlag.INTERVIEW_LOCATION_PERMISSIONS_REQUIRED,
    { defaultValue: false }
  );
  const isChildcareSupportChatEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_CHILDCARE_SUPPORT_CHAT, {
    defaultValue: false,
  });
  const { screeningCalendlyLink, upskillingCalendlyLink, showCallMeNow } =
    usePlacementRecruiterScreeningConfig();

  // Derive data
  const highestRankingSharedQualification = getHighestRankingQualification(
    (placementDetails.workerTypes ?? []).filter((qualification) =>
      worker.licensesData.map((licenseData) => licenseData.qualification).includes(qualification)
    )
  );
  const isSignOnBonusPaid = signOnBonuses?.data.some(
    (incentive) => incentive.attributes.status === "paid"
  );

  // Prioritize in-person interviews over phone screen interviews
  const bookedInterviews = placementDetails.interviews.filter(
    (interview) => interview.attributes.status === InterviewStatus.BOOKED
  );
  const phoneScreenInterview = bookedInterviews.find(
    (interview) => interview.attributes.type === "phone-screen"
  );
  const inPersonInterview = bookedInterviews.find(
    (interview) => interview.attributes.type === "in-person-interview"
  );
  const interview = inPersonInterview ?? phoneScreenInterview ?? bookedInterviews[0];

  const hasApplied = hasAppliedToPlacement(placementDetails);

  // Calculate workplace start hour for today reschedule checks
  const workplaceStartHour =
    interview && placementDetails.workplace.reliabilityBanRule
      ? getWorkplaceStartHour({
          interviewStart: parseISO(interview.attributes.start),
          workerTimezone,
          workplaceTimezone: placementDetails.workplace.tmz,
          startHour: placementDetails.workplace.reliabilityBanRule.startHour,
        })
      : undefined;

  const isReschedulingPastInterview =
    interview?.attributes.start !== undefined &&
    isBefore(parseISO(interview.attributes.start), new Date());

  const isSchedulingPastWorkplaceStartHour = workplaceStartHour
    ? isAfter(new Date(), zonedTimeToUtc(workplaceStartHour, workerTimezone))
    : false;

  // Use the new simplified reliability rule utility
  const isLateCancellation =
    hiringReliability.enabled && interview?.attributes.start
      ? isInterviewLateCancellation({
          interviewStart: parseISO(interview.attributes.start),
          interviewCreatedAt: interview.attributes.createdAt,
          isSchedulingPastWorkplaceStartHour,
          hiringReliability: {
            simplifiedBanEnabled: hiringReliability.simplifiedBanEnabled,
            workplaceStartHour: placementDetails.workplace.reliabilityBanRule?.startHour ?? 8,
            maxLateCancellationHours: hiringReliability.maxLateCancellationHours,
            graceWindowInMinutes: hiringReliability.graceWindowInMinutes,
          },
        })
      : false;

  const startsInWorkplaceToday =
    interview &&
    isSameDay(
      utcToZonedTime(new Date(), placementDetails.workplace.tmz),
      utcToZonedTime(parseISO(interview.attributes.start), placementDetails.workplace.tmz)
    );

  const isSchedulingTodayAfterWorkplaceStartHour = Boolean(
    workplaceStartHour &&
      interview &&
      isAfter(new Date(), zonedTimeToUtc(workplaceStartHour, placementDetails.workplace.tmz)) &&
      startsInWorkplaceToday
  );

  const isProfileCompletedOnPlacementDetailsPage = checkIsProfileCompletedForPlacement({
    isProfileCompleted,
    highestRankingSharedQualification,
    placementWorkerTypes: placementDetails.workerTypes,
    autoApprovedQualifications,
  });

  // Build params and get primary action
  const primaryActionParams = buildPrimaryActionParams(
    placementDetails,
    placementCandidate,
    interviewAvailability,
    {
      isLoadingInterviewAvailability,
      isProfileCompleted: isProfileCompletedOnPlacementDetailsPage,
      isProfilePending,
      isProfileInvalid,
      requirement,
      talkToRecruiterCopy,
      isJobInterviewsEnabled,
      isJobRequestEnabled,
      isHiringReferralEnabled,
      maxReferralBonusAmount,
      isOnboardingRequirementsLoading,
      isGetIncentivesLoading,
      isReschedulingPastInterview,
      simplifiedBanEnabled: hiringReliability.simplifiedBanEnabled,
      startsInWorkplaceToday,
      bookedInterviewType: interview?.attributes.type,
      isChildcareSupportChatEnabled,
      autoApprovedQualifications,
      workerShoppableQualificationIds:
        shoppableQualifications?.map((sq) => sq.relationships.qualification.data?.id) ?? [],
      allQualifications: allQualificationsData?.data ?? [],
      isQualificationsLoading,
    }
  );

  const primaryAction = getPrimaryAction(primaryActionParams);

  const eventContext = {
    workerId,
    placementCandidateId: placementCandidate.id,
    placementId: placementDetails.id,
    placementApplicationId: placementDetails.applications?.[0]?.id,
    isProspectWorkplace: Boolean(placementDetails.workplace.placementSettings?.isProspect),
  };

  return {
    primaryAction,
    primaryActionParams,
    // Expose derived data that operations layer needs
    derivedData: {
      workerId,
      workerTimezone,
      interview,
      hasApplied,
      isSignOnBonusPaid,
      incentiveProgram,
      highestRankingSharedQualification,
      hiringReliability,
      requirement,
      isProfilePending,
      isLateCancellation,
      isReschedulingPastInterview,
      workplaceStartHour,
      isSchedulingTodayAfterWorkplaceStartHour,
      startsInWorkplaceToday,
      eventContext,
      screeningCalendlyLink,
      upskillingCalendlyLink,
      showCallMeNow,
      talkToRecruiterCopy,
      isInterviewLocationPermissionsCheckEnabled,
      isInterviewLocationPermissionsRequired,
      isJobRequestEnabled,
      missingAutoApprovedQualificationId: primaryActionParams.missingAutoApprovedQualificationId,
    },
  };
}

export type PlacementPrimaryActionReturn = ReturnType<typeof usePlacementPrimaryAction>;
export type PlacementDerivedData = PlacementPrimaryActionReturn["derivedData"];
